function pad(n) {
  return String(n).padStart(2, '0');
}

function formatDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatLocalDateTime(d) {
  return `${formatDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const WEEKDAY_NAME_TO_INDEX = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function parseWeekStartDay(value) {
  if (typeof value === 'number' && value >= 0 && value <= 6) return value;
  const key = String(value || 'monday').trim().toLowerCase();
  return WEEKDAY_NAME_TO_INDEX[key] ?? 1;
}

/** Monday-based week start (matches frontend startOfWeek with weekStartsOn: 1). */
function getWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return formatDate(d);
}

function getWeekStartForDate(dateStr, weekStartsOn) {
  const weekday = parseWeekStartDay(weekStartsOn);
  const d = new Date(`${dateStr}T00:00:00`);
  const diff = (d.getDay() - weekday + 7) % 7;
  d.setDate(d.getDate() - diff);
  return formatDate(d);
}

function collectParamDates(params) {
  const dates = [];
  const seed = params?.seed || {};
  for (const row of [...(seed.shifts || []), ...(seed.attendance || [])]) {
    if (row.date) dates.push(row.date);
    if (row.endDate) dates.push(row.endDate);
    if (row.clockOutDate) dates.push(row.clockOutDate);
  }
  for (const step of params?.steps || []) {
    if (step.addManualDay?.date) dates.push(step.addManualDay.date);
    for (const att of step.addAttendance || []) {
      if (att.date) dates.push(att.date);
    }
    for (const shift of step.addShifts || []) {
      if (shift.date) dates.push(shift.date);
    }
  }
  return dates;
}

/** Snap scenario weekStart to the tenant week-start day, using seed dates when present. */
function alignScenarioWeekStart(params, weekStartsOn, fallbackWeekStart) {
  const seedDates = collectParamDates(params).sort();
  const basis = seedDates[0] || params.weekStart || fallbackWeekStart;
  if (!basis) return params;
  return { ...params, weekStart: getWeekStartForDate(basis, weekStartsOn) };
}

function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return formatDate(d);
}

/** Normalize datetimes for comparison (timezone-naive wall clock, ignore subseconds). */
function normalizeTime(value) {
  if (!value) return null;
  return String(value)
    .replace(' ', 'T')
    .replace(/\.\d+Z?$/i, '')
    .replace(/Z$/i, '')
    .slice(0, 19);
}

/** Extract yyyy-MM-dd without timezone conversion (matches Attendance getClockInDateKey). */
function dateFromIso(value) {
  if (!value) return '';
  const s = String(value);
  if (s.includes('T')) return s.split('T')[0];
  const spaceIndex = s.indexOf(' ');
  if (spaceIndex > 0) return s.substring(0, spaceIndex);
  return s.slice(0, 10);
}

/** Build local datetime string for API (no timezone suffix). */
function localDateTime(dateStr, timeStr) {
  const [h, m, s = '00'] = timeStr.split(':');
  return `${dateStr}T${pad(Number(h))}:${pad(Number(m))}:${pad(Number(s))}`;
}

/** Compare wall-clock times; expected may be HH:mm or full yyyy-MM-ddTHH:mm:ss. */
function timesEqual(actual, expected) {
  const a = normalizeTime(actual);
  const e = normalizeTime(expected);
  if (!e) return !a;
  if (!a) return false;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(e)) {
    const timePart = a.includes('T') ? a.split('T')[1] : a;
    return timePart.slice(0, e.length) === e;
  }
  return a === e;
}

export {
  getWeekStart,
  getWeekStartForDate,
  parseWeekStartDay,
  alignScenarioWeekStart,
  collectParamDates,
  addDays,
  normalizeTime,
  timesEqual,
  dateFromIso,
  formatDate,
  localDateTime,
  formatLocalDateTime,
};
